import { SegmentedControl, Select, Table } from "@mantine/core";
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import BaseTable from "#/components/tables/BaseTable";
import { EmptyState } from "#/components/ui/EmptyState";
import { useClient } from "#/hook/useClient";
import ReportSection from "#/pages/reports/components/ReportSection";
import { exportReportToPdf } from "#/pages/reports/utils/exportPdf";
import type { PrvPattern, PrvPatternsView } from "#/types/prv";
import { formatDate, formatDateTime, getDateFormat } from "#/utils/dateTimeUtils";
import PrvCategoryCards, { type PrvCategoryCard } from "./PrvCategoryCards";
import PrvGlossary from "./PrvGlossary";
import PrvHeatmap, { type PrvSelection } from "./PrvHeatmap";
import PrvSummaryBox from "./PrvSummaryBox";
import PrvTargetBadge from "./PrvTargetBadge";
import PrvTimelineChart from "./PrvTimelineChart";
import {
  averageStaySeconds,
  buildTimeline,
  checkTarget,
  formatDuration,
  itemsPerPeriod,
  nextScheduledRun,
  type PrvBucket,
  rhythmPeriodDays,
  runDurationSeconds,
  targetPerBucket,
} from "./prvUtils";
import { usePrvTranslation } from "./usePrvTranslation";

export interface PrvPatternsPanelHandle {
  /** Render the panel as displayed into a paginated PDF. */
  exportToPdf: (filename: string) => Promise<void>;
}

interface Props {
  view: PrvPatternsView;
  /** Pin the view to one sub-client: no sub-client selector, everything scoped to it. */
  lockedSubClientId?: number;
  /** Show the "last run / next run / config" strip (TMA admin view). */
  showRunSummary?: boolean;
  /** Empty-state copy when the view has no patterns at all. */
  emptyTitle?: string;
  emptyDescription?: string;
}

const ALL = "all";

/** Patterns matching the current selection (null = all on that axis). */
function scopePatterns(view: PrvPatternsView, selection: PrvSelection): PrvPattern[] {
  return view.patterns.filter(
    (pattern) =>
      (selection.subClientId === null || pattern.subClientId === selection.subClientId) &&
      (selection.productId === null || pattern.productId === selection.productId)
  );
}

/** One card per category over the scoped patterns (aggregated across sub-clients when several). */
function buildCards(
  view: PrvPatternsView,
  scoped: PrvPattern[],
  singleSubClient: boolean,
  asOf: string
): PrvCategoryCard[] {
  const byProduct = new Map<number, PrvPattern[]>();
  for (const pattern of scoped) {
    const list = byProduct.get(pattern.productId);
    if (list) list.push(pattern);
    else byProduct.set(pattern.productId, [pattern]);
  }
  const cards: PrvCategoryCard[] = [];
  for (const [productId, patterns] of byProduct) {
    const category = view.categories.find((c) => c.id === productId);
    const stays = patterns.reduce((sum, p) => sum + p.stays, 0);
    const completedStays = patterns.reduce((sum, p) => sum + p.completedStays, 0);
    const totalSeconds = patterns.reduce((sum, p) => sum + p.totalStaySeconds, 0);
    const present = patterns.reduce((sum, p) => sum + p.present, 0);
    const first = patterns
      .map((p) => p.firstMovementAt)
      .filter((d): d is string => d !== null)
      .sort()[0];
    const last = patterns
      .map((p) => p.lastMovementAt)
      .filter((d): d is string => d !== null)
      .sort()
      .at(-1);
    // A target belongs to one sub-client × category, so it is only checked
    // when the card stands for exactly that pair.
    const pair = singleSubClient && patterns.length === 1 ? patterns[0] : null;
    const merged: PrvPattern = {
      id: null,
      subClientId: patterns[0].subClientId,
      productId,
      stays,
      completedStays,
      totalStaySeconds: totalSeconds,
      firstMovementAt: first ?? null,
      lastMovementAt: last ?? null,
      present,
      target: pair?.target ?? null,
    };
    const periodDays = rhythmPeriodDays(merged);
    const perPeriod = itemsPerPeriod(merged, periodDays);
    cards.push({
      productId,
      name: category?.name ?? `#${productId}`,
      color: category?.color ?? null,
      present,
      arrivals: stays,
      departures: completedStays,
      averageStaySeconds: averageStaySeconds(merged),
      rate: perPeriod === null ? null : { arrivals: perPeriod, days: periodDays },
      firstMovementAt: merged.firstMovementAt,
      lastMovementAt: merged.lastMovementAt,
      target: pair ? checkTarget(pair, view.days, asOf) : null,
    });
  }
  return cards.sort(
    (a, b) => b.present - a.present || b.arrivals - a.arrivals || a.name.localeCompare(b.name)
  );
}

/**
 * The PRV results themselves: summary, filters, heatmap, timeline, category
 * cards and their table twin. Used by the TMA-admin drawer (all sub-clients,
 * with the run strip) and read-only on a sub-client's own page (pinned to it).
 */
const PrvPatternsPanel = forwardRef<PrvPatternsPanelHandle, Props>(
  ({ view, lockedSubClientId, showRunSummary = true, emptyTitle, emptyDescription }, ref) => {
    const t = usePrvTranslation();
    // Dates follow the logged-in user's client format; a TMA admin (no client) gets the default.
    const { client } = useClient();
    const rootRef = useRef<HTMLDivElement>(null);
    const [selection, setSelectionState] = useState<PrvSelection>({
      subClientId: lockedSubClientId ?? null,
      productId: null,
    });
    const [bucket, setBucket] = useState<PrvBucket>("week");
    const [viewMode, setViewMode] = useState<"charts" | "table">("charts");

    /** Selection changes never move off a pinned sub-client. */
    const setSelection = (update: PrvSelection | ((prev: PrvSelection) => PrvSelection)) =>
      setSelectionState((prev) => {
        const next = typeof update === "function" ? update(prev) : update;
        return lockedSubClientId === undefined ? next : { ...next, subClientId: lockedSubClientId };
      });

    useImperativeHandle(ref, () => ({
      exportToPdf: async (filename: string) => {
        if (rootRef.current) await exportReportToPdf(rootRef.current, filename);
      },
    }));

    const scoped = useMemo(() => scopePatterns(view, selection), [view, selection]);
    const timeline = useMemo(() => {
      const ids = new Set(scoped.map((p) => p.id).filter((id): id is number => id !== null));
      return buildTimeline(view.days, ids, bucket, getDateFormat(client));
    }, [view, scoped, bucket, client]);
    const timelineTarget = useMemo(() => {
      if (selection.subClientId === null || selection.productId === null || scoped.length !== 1) {
        return null;
      }
      return targetPerBucket(scoped[0].target, bucket);
    }, [selection, scoped, bucket]);
    // Cards ignore the category selection on purpose: all categories stay on
    // screen and the unselected ones fade, so the selection is visible and
    // reversible instead of making the other cards vanish.
    // Targets are checked against the latest interval ending at the last run.
    const asOf = view.lastRun?.startedAt ?? view.generatedAt;
    const cards = useMemo(
      () =>
        buildCards(
          view,
          scopePatterns(view, { subClientId: selection.subClientId, productId: null }),
          selection.subClientId !== null,
          asOf
        ),
      [view, selection.subClientId, asOf]
    );
    const activeCategoryIds = useMemo(() => {
      const ids = new Set<number>();
      for (const pattern of view.patterns) ids.add(pattern.productId);
      return ids;
    }, [view]);

    const nameOfSub = (id: number) => view.subClients.find((s) => s.id === id)?.name ?? `#${id}`;
    const nameOfCategory = (id: number) =>
      view.categories.find((c) => c.id === id)?.name ?? `#${id}`;

    const lastRun = view.lastRun ?? null;
    const nextRun = nextScheduledRun(view.config.schedule, lastRun?.startedAt ?? null);
    const selectionTitle =
      selection.subClientId !== null && selection.productId !== null
        ? `${nameOfSub(selection.subClientId)} · ${nameOfCategory(selection.productId)}`
        : selection.subClientId !== null
          ? nameOfSub(selection.subClientId)
          : selection.productId !== null
            ? nameOfCategory(selection.productId)
            : t("clients.prv.results.allSubClients");

    return (
      <div ref={rootRef} className="flex flex-col gap-4 bg-white">
        {showRunSummary && (
          <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600">
            <span>
              <span className="font-semibold text-gray-800">
                {t("clients.prv.results.summary.lastRun")}:
              </span>{" "}
              {lastRun
                ? `${formatDateTime(lastRun.startedAt, client)} ${t("clients.prv.results.summary.by")} ${
                    lastRun.triggeredBy?.name ?? t("clients.prv.runLog.scheduler")
                  }`
                : t("clients.prv.actions.neverRun")}
            </span>
            {lastRun && (
              <>
                <span>
                  <span className="font-semibold text-gray-800">
                    {t("clients.prv.results.summary.duration")}:
                  </span>{" "}
                  {formatDuration(runDurationSeconds(lastRun), t)}
                </span>
                <span>
                  <span className="font-semibold text-gray-800">
                    {t("clients.prv.results.summary.rowsRead")}:
                  </span>{" "}
                  {lastRun.rowsRead}
                </span>
                <span>
                  <span className="font-semibold text-gray-800">
                    {t("clients.prv.results.summary.staysConfirmed")}:
                  </span>{" "}
                  {lastRun.staysConfirmed}
                </span>
              </>
            )}
            <span>
              <span className="font-semibold text-gray-800">
                {t("clients.prv.results.summary.nextRun")}:
              </span>{" "}
              {nextRun ? formatDateTime(nextRun, client) : t("clients.prv.schedule.paused")}
            </span>
            <span>
              {t("clients.prv.results.summary.config", {
                radius: view.config.radiusMeters,
                minutes: view.config.minDwellMinutes,
              })}
            </span>
          </div>
        )}

        {view.patterns.length === 0 ? (
          <EmptyState
            title={emptyTitle ?? t("clients.prv.results.noData")}
            description={emptyDescription ?? t("clients.prv.results.noDataHint")}
          />
        ) : (
          <>
            <PrvSummaryBox view={view} scoped={scoped} selection={selection} />

            {/* One filter row scoping everything below it: dropdowns left, Charts/Table toggle right */}
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-3">
                {lockedSubClientId === undefined && (
                  <Select
                    size="xs"
                    label={t("clients.prv.results.subClient")}
                    value={selection.subClientId === null ? ALL : String(selection.subClientId)}
                    onChange={(value) =>
                      setSelection((prev) => ({
                        ...prev,
                        subClientId: !value || value === ALL ? null : Number(value),
                      }))
                    }
                    data={[
                      { value: ALL, label: t("clients.prv.results.allSubClients") },
                      ...view.subClients.map((sub) => ({ value: String(sub.id), label: sub.name })),
                    ]}
                    allowDeselect={false}
                    searchable
                    w={240}
                  />
                )}
                <Select
                  size="xs"
                  label={t("clients.prv.results.category")}
                  value={selection.productId === null ? ALL : String(selection.productId)}
                  onChange={(value) =>
                    setSelection((prev) => ({
                      ...prev,
                      productId: !value || value === ALL ? null : Number(value),
                    }))
                  }
                  data={[
                    { value: ALL, label: t("clients.prv.results.allCategories") },
                    ...view.categories
                      .filter((category) => activeCategoryIds.has(category.id))
                      .map((category) => ({ value: String(category.id), label: category.name })),
                  ]}
                  allowDeselect={false}
                  searchable
                  w={240}
                />
              </div>
              <div className="flex items-end gap-3">
                <SegmentedControl
                  size="xs"
                  value={viewMode}
                  onChange={(value) => setViewMode(value === "table" ? "table" : "charts")}
                  data={[
                    { value: "charts", label: t("clients.prv.results.view.charts") },
                    { value: "table", label: t("clients.prv.results.view.table") },
                  ]}
                />
              </div>
            </div>

            {viewMode === "charts" ? (
              <div className="flex flex-col gap-5 pb-4">
                {/* The heatmap compares sub-clients; pinned to one it would be a single row. */}
                {lockedSubClientId === undefined && (
                  <ReportSection
                    title={t("clients.prv.results.heatmap.title")}
                    subtitle={t("clients.prv.results.heatmap.subtitle")}
                  >
                    <PrvHeatmap view={view} selection={selection} onSelect={setSelection} />
                  </ReportSection>
                )}

                {/* Weekly / Monthly only changes this chart, so the toggle lives on it. */}
                <ReportSection
                  title={t("clients.prv.results.timeline.title")}
                  subtitle={selectionTitle}
                  rightSection={
                    <SegmentedControl
                      size="xs"
                      value={bucket}
                      onChange={(value) => setBucket(value === "month" ? "month" : "week")}
                      data={[
                        { value: "week", label: t("clients.prv.results.bucket.week") },
                        { value: "month", label: t("clients.prv.results.bucket.month") },
                      ]}
                    />
                  }
                >
                  <PrvTimelineChart points={timeline} target={timelineTarget} bucket={bucket} />
                </ReportSection>

                <ReportSection
                  title={t("clients.prv.results.cards.title")}
                  subtitle={
                    selection.subClientId !== null
                      ? nameOfSub(selection.subClientId)
                      : t("clients.prv.results.allSubClients")
                  }
                  rightSection={<PrvGlossary showHeatmap={lockedSubClientId === undefined} />}
                >
                  <PrvCategoryCards
                    cards={cards}
                    selectedProductId={selection.productId}
                    showTarget={selection.subClientId !== null}
                    asOf={asOf}
                    onSelect={(productId) =>
                      setSelection((prev) => ({
                        ...prev,
                        productId: prev.productId === productId ? null : productId,
                      }))
                    }
                  />
                </ReportSection>
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-auto pb-4">
                <BaseTable
                  headers={[
                    t("clients.prv.results.subClient"),
                    t("clients.prv.results.category"),
                    t("clients.prv.results.table.arrivals"),
                    t("clients.prv.results.table.departures"),
                    t("clients.prv.results.table.present"),
                    t("clients.prv.results.table.rate"),
                    t("clients.prv.results.table.avgTime"),
                    t("clients.prv.results.table.first"),
                    t("clients.prv.results.table.last"),
                    t("clients.prv.results.table.target"),
                  ]}
                  rows={scoped.map((pattern) => {
                    const periodDays = rhythmPeriodDays(pattern);
                    const perPeriod = itemsPerPeriod(pattern, periodDays);
                    return (
                      <Table.Tr key={`${pattern.subClientId}:${pattern.productId}`}>
                        <Table.Td>{nameOfSub(pattern.subClientId)}</Table.Td>
                        <Table.Td>{nameOfCategory(pattern.productId)}</Table.Td>
                        <Table.Td className="tabular-nums">{pattern.stays}</Table.Td>
                        <Table.Td className="tabular-nums">{pattern.completedStays}</Table.Td>
                        <Table.Td className="tabular-nums">{pattern.present}</Table.Td>
                        <Table.Td>
                          {perPeriod === null
                            ? "--"
                            : t("clients.prv.results.arrivalsPerDays", {
                                arrivals: perPeriod.toFixed(1),
                                days: periodDays,
                              })}
                        </Table.Td>
                        <Table.Td>{formatDuration(averageStaySeconds(pattern), t)}</Table.Td>
                        <Table.Td>{formatDate(pattern.firstMovementAt, client)}</Table.Td>
                        <Table.Td>{formatDate(pattern.lastMovementAt, client)}</Table.Td>
                        <Table.Td>
                          <PrvTargetBadge
                            check={checkTarget(pattern, view.days, asOf)}
                            asOf={asOf}
                          />
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }
);

PrvPatternsPanel.displayName = "PrvPatternsPanel";

export default PrvPatternsPanel;
